import {Animated, Dimensions, Easing, Pressable, View} from "react-native"
import React, {useRef} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import Link from "@kaspernj/api-maker/build/link"
import memo from "set-state-compare/build/memo"
import {Portal} from "conjointment"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {Text} from "shared/base"
import useEventListener from "ya-use-event-listener"
import usePressOutside from "outside-eye/build/use-press-outside"

const HoverMenuItem = memo(shapeComponent(class HoverMenuItem extends ShapeComponent {
  render() {
    return (
      <Link {...this.props}>
        <Text style={this.textStyle ||= {marginTop: 4, marginBottom: 4, color: "black", fontSize: 16}}>
          {this.p.label}
        </Text>
      </Link>
    )
  }
}))

const HoverMenu = memo(shapeComponent(class HoverMenu extends ShapeComponent {
  width = 300
  windowHeight = Dimensions.get("window").height
  windowWidth = Dimensions.get("window").width

  popupMenuLeft = new Animated.Value(0)
  popupMenuTop = new Animated.Value(0)

  static defaultProps = {
    optionsPortal: false
  }

  static propTypes = propTypesExact({
    children: PropTypes.node,
    content: PropTypes.func.isRequired,
    optionsPortal: PropTypes.bool.isRequired
  })

  setup() {
    this.viewRef = useRef()

    useEventListener(Dimensions, "change", this.tt.onDimensionsChange)
    usePressOutside(this.tt.viewRef, this.tt.onClickOutsideHoverMenu)

    this.useStates({
      clientX: undefined,
      clientY: undefined,
      hoverMenuHeight: null,
      hoverMenuWidth: null,
      popupMenuPosition: null,
      showMenu: undefined
    })
  }

  render() {
    const {onMouseMove, onPress} = this.tt
    const {children} = this.props

    return (
      <>
        <Pressable onMouseDown={onMouseMove} onMouseMove={onMouseMove} onPress={onPress} testID="components/hover-menu/index/pressable">
          {children}
        </Pressable>
        {this.s.showMenu && this.p.optionsPortal &&
          <Portal name="hover-menu">
            {this.hoverMenuContent()}
          </Portal>
        }
        {this.s.showMenu && !this.p.optionsPortal &&
          <View>
            {this.hoverMenuContent()}
          </View>
        }
      </>
    )
  }

  hoverMenuContent() {
    const {viewRef, width} = this.tt
    const {content} = this.props

    return (
      <Animated.View
        onLayout={this.tt.onHoverMenuLayout}
        ref={viewRef}
        style={this.cache("hoverMenuStyle", {
          position: "absolute",
          left: this.tt.popupMenuLeft,
          top: this.tt.popupMenuTop,
          zIndex: 1000,
          padding: 20,
          width,
          backgroundColor: "#fff",
          border: "1px solid #f3f3f3",
          borderRadius: 7,
          boxShadow: "0px 8px 10px 0px rgba(0, 0, 0, 0.0706)"
        }, [width])}
        testID="components/hover-menu/index/content"
      >
        {content()}
      </Animated.View>
    )
  }

  positionHoverMenu(mode) {
    const {optionsPortal} = this.p
    const {popupMenuClientX, popupMenuClientY, windowHeight, windowWidth} = this.tt
    const {hoverMenuHeight, hoverMenuWidth} = this.s
    const furthestX = popupMenuClientX + hoverMenuWidth
    const furthestY = popupMenuClientY + hoverMenuHeight
    let popupMenuLeft, popupMenuPositionX, popupMenuPositionY, popupMenuTop

    if (furthestX >= windowWidth) {
      popupMenuPositionX = "left"

      if (optionsPortal) {
        popupMenuLeft = popupMenuClientX - hoverMenuWidth
      } else {
        popupMenuLeft = -hoverMenuWidth
      }
    } else {
      popupMenuPositionX = "right"

      if (optionsPortal) {
        popupMenuLeft = popupMenuClientX
      } else {
        popupMenuLeft = 0
      }
    }

    if (furthestY >= windowHeight) {
      popupMenuPositionY = "top"

      if (optionsPortal) {
        popupMenuTop = popupMenuClientY - hoverMenuHeight
      } else {
        popupMenuTop = -hoverMenuHeight
      }
    } else {
      popupMenuPositionY = "below"

      if (optionsPortal) {
        popupMenuTop = popupMenuClientY
      } else {
        popupMenuTop = 0
      }
    }

    if (popupMenuPositionX != this.tt.popupMenuPositionX) {
      this.popupMenuPositionX = popupMenuPositionX

      Animated
        .timing(this.tt.popupMenuLeft, {
          duration: 200,
          easing: Easing.inOut(Easing.linear),
          toValue: popupMenuLeft,
          useNativeDriver: true
        })
        .start()
    }

    if (popupMenuPositionY != this.tt.popupMenuPositionY) {
      this.popupMenuPositionY = popupMenuPositionY

      if (mode == "dimensionsChange") {
        Animated
          .timing(this.tt.popupMenuTop, {
            duration: 200,
            easing: Easing.inOut(Easing.linear),
            toValue: popupMenuTop,
            useNativeDriver: true
          })
          .start()
      } else {
        this.tt.popupMenuTop.setValue(popupMenuTop)
      }
    }
  }

  closeMenu() {
    this.setState({
      hoverMenuHeight: null,
      hoverMenuWidth: null,
      showMenu: false
    })
  }

  onClickOutsideHoverMenu = () => {
    if (this.s.showMenu) {
      setTimeout(() => this.closeMenu(), 0)
    } else {
      this.closeMenu()
    }
  }

  onHoverMenuLayout = ({nativeEvent}) => {
    const {height, width} = nativeEvent.layout

    this.setState({
      hoverMenuHeight: height,
      hoverMenuWidth: width
    })
    this.positionHoverMenu()
  }

  onMouseMove = (e) => {
    this.clientX = e.clientX + document.documentElement.scrollLeft
    this.clientY = e.clientY + document.documentElement.scrollTop
  }

  onPress = () => {
    const {optionsPortal} = this.p
    const {clientX, clientY, width, windowWidth} = this.tt
    const furthestX = clientX + width
    let popupMenuLeft, popupMenuPositionX, popupMenuTop

    if (furthestX >= windowWidth) {
      popupMenuPositionX = "left"

      if (optionsPortal) {
        popupMenuLeft = clientX - width
      } else {
        popupMenuLeft = -width
      }
    } else {
      popupMenuPositionX = "right"

      if (optionsPortal) {
        popupMenuLeft = clientX
      } else {
        popupMenuLeft = 0
      }
    }

    if (optionsPortal) {
      popupMenuTop = clientY
    } else {
      popupMenuTop = 0
    }

    this.popupMenuClientX = clientX
    this.popupMenuClientY = clientY

    this.tt.popupMenuLeft.setValue(popupMenuLeft)
    this.tt.popupMenuTop.setValue(popupMenuTop)
    this.popupMenuPositionX = popupMenuPositionX
    this.popupMenuPositionY = "below"
    this.setState({showMenu: true})
  }

  onDimensionsChange = ({window}) => {
    this.windowWidth = window.width
    this.windowHeight = window.height

    if (this.s.hoverMenuWidth && this.s.hoverMenuHeight) {
      this.positionHoverMenu("dimensionsChange")
    }
  }
}))

export {HoverMenu, HoverMenuItem}
